import * as React from "react";
import type { Session, SupabaseClient, User } from "@supabase/supabase-js";
import { getSupabaseClient } from "./client";

export interface DesktopAuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isSessionReady: boolean;
  isConfigured: boolean;
}

interface DesktopAuthContextValue extends DesktopAuthState {
  signInWithPassword: (email: string, password: string) => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const DesktopAuthContext = React.createContext<DesktopAuthContextValue | null>(null);

export function useDesktopAuth(): DesktopAuthContextValue {
  const value = React.useContext(DesktopAuthContext);
  if (!value) {
    throw new Error("useDesktopAuth must be used within DesktopAuthProvider");
  }
  return value;
}

function getConfiguredClient(): SupabaseClient | null {
  return getSupabaseClient();
}

export function DesktopAuthProvider({ children }: { children: React.ReactNode }) {
  const supabase = React.useMemo(() => getConfiguredClient(), []);
  const [state, setState] = React.useState<DesktopAuthState>({
    user: null,
    session: null,
    loading: Boolean(supabase),
    isSessionReady: false,
    isConfigured: Boolean(supabase),
  });

  React.useEffect(() => {
    if (!supabase) {
      setState((current) => ({
        ...current,
        loading: false,
        isSessionReady: false,
        isConfigured: false,
      }));
      return;
    }

    let cancelled = false;

    const applySession = (session: Session | null) => {
      if (cancelled) return;
      const expiresAt = session?.expires_at ? session.expires_at * 1000 : 0;
      const validSession = Boolean(session?.access_token && session.user && expiresAt > Date.now());
      const nextSession = validSession ? session : null;
      setState({
        user: nextSession?.user ?? null,
        session: nextSession,
        loading: false,
        isSessionReady: Boolean(nextSession),
        isConfigured: true,
      });
      try {
        if (nextSession) {
          localStorage.setItem("ares_last_uid", nextSession.user.id);
        } else {
          localStorage.removeItem("ares_last_uid");
        }
      } catch {
        // Storage can be unavailable in private browsing; auth remains in memory.
      }
    };

    supabase.auth.getSession().then(({ data }) => {
      applySession(data.session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [supabase]);

  const signInWithPassword = React.useCallback(
    async (email: string, password: string) => {
      if (!supabase) throw new Error("Supabase is not configured");
      const cleanEmail = email.trim().toLowerCase();
      if (!cleanEmail || !password) throw new Error("Email and password are required");
      const { error } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password,
      });
      if (error) throw error;
    },
    [supabase],
  );

  const sendMagicLink = React.useCallback(
    async (email: string) => {
      if (!supabase) throw new Error("Supabase is not configured");
      const cleanEmail = email.trim().toLowerCase();
      if (!cleanEmail) throw new Error("Email is required");
      const redirectTo =
        typeof window !== "undefined"
          ? `${window.location.origin}/?view=app#/settings`
          : undefined;
      const { error } = await supabase.auth.signInWithOtp({
        email: cleanEmail,
        options: redirectTo ? { emailRedirectTo: redirectTo } : undefined,
      });
      if (error) throw error;
    },
    [supabase],
  );

  const signOut = React.useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut({ scope: "global" });
    setState({
      user: null,
      session: null,
      loading: false,
      isSessionReady: false,
      isConfigured: true,
    });
  }, [supabase]);

  const value = React.useMemo<DesktopAuthContextValue>(
    () => ({
      ...state,
      signInWithPassword,
      sendMagicLink,
      signOut,
    }),
    [sendMagicLink, signInWithPassword, signOut, state],
  );

  return <DesktopAuthContext.Provider value={value}>{children}</DesktopAuthContext.Provider>;
}
